import { ChangeDetectionStrategy, Component, effect, inject, input, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { OnBoardingService } from '../../services/on-boarding.service';
import { RELEASE_ONBOARDING_CONFIG, hasCompletedToursBeforeDeployment } from './release-onboarding.config';

@Component({
  selector: 'app-release-onboarding',
  standalone: true,
  imports: [TranslateModule],
  templateUrl: './release-onboarding.component.html',
  styleUrl: './release-onboarding.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReleaseOnboardingComponent {
  private readonly onboarding = inject(OnBoardingService);
  readonly noProjectsAvailable = input(false);
  readonly appReady = input(false);
  readonly visible = signal(false);
  readonly currentStep = signal(0);
  readonly dontShowForFutureReleases = signal(false);
  private readonly eligible = signal(false);
  private readonly eligibilityChecked = signal(false);
  private readonly dismissed = signal(false);
  private readonly seenKey = `release-onboarding-seen-${RELEASE_ONBOARDING_CONFIG.releaseId}`;

  constructor() {
    effect(() => {
      if (!this.appReady()) return;

      if (!this.eligibilityChecked()) {
        this.eligible.set(this.isEligibleForRelease());
        this.eligibilityChecked.set(true);
      }

      if (this.eligible() && !this.dismissed() && !this.onboarding.isTourActive()) {
        this.visible.set(true);
      }
    });
  }

  private isEligibleForRelease(): boolean {
    const now = Date.now();
    const deploymentDate = Date.parse(`${RELEASE_ONBOARDING_CONFIG.deploymentDate}T00:00:00Z`);
    if (
      !Number.isFinite(deploymentDate) ||
      now < deploymentDate ||
      localStorage.getItem(this.seenKey) ||
      localStorage.getItem('release-onboarding-never-show')
    ) return false;

    const allToursCompletedBeforeDeployment = hasCompletedToursBeforeDeployment(
      RELEASE_ONBOARDING_CONFIG.requiredDriverTours,
      key => this.onboarding.getCompletionTimestampForRelease(key, deploymentDate),
      deploymentDate,
    );
    return (
      allToursCompletedBeforeDeployment ||
      this.onboarding.hasSkippedOnboardingBefore(deploymentDate)
    );
  }

  next(): void {
    if (this.currentStep() === 0) {
      this.currentStep.set(1);
      return;
    }
    this.close();
  }

  previous(): void {
    this.currentStep.set(0);
  }

  close(): void {
    localStorage.setItem(this.seenKey, new Date().toISOString());
    if (this.dontShowForFutureReleases()) {
      localStorage.setItem('release-onboarding-never-show', 'true');
    }
    this.dismissed.set(true);
    this.visible.set(false);
  }
}
